import { motion } from "framer-motion";
import { Lock, Users, Shield, Server, Check } from "lucide-react";

const features = [
  {
    title: "Three-party trust model",
    description: "Buyer, PROVENANCE, and Seller each have defined access boundaries. No data leakage.",
  },
  {
    title: "Encrypted artefact handling",
    description: "All code and documentation is encrypted at rest and in transit throughout the engagement.",
  },
  {
    title: "SOC 2 aligned",
    description: "Enterprise-grade security controls with continuous compliance monitoring.",
  },
  {
    title: "Full audit trail",
    description: "Every access, every finding, every decision is logged and attributable.",
  },
];

export const SovereigntyModel = () => {
  return (
    <section className="relative py-24">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/20 to-background" />
      
      <div className="container relative mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 text-primary font-medium text-sm uppercase tracking-wider">
            <Lock className="h-4 w-4" />
            Security
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4 mb-6">
            Sovereignty by design
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Your target's intellectual property never leaves a secure, controlled environment. 
            We take data sovereignty as seriously as you do.
          </p>
        </motion.div>

        {/* Trust Model Diagram */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl border border-primary/30 bg-card/50 p-8 mb-12"
        >
          <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-12">
            {/* Buyer */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center p-6 rounded-xl border border-border bg-background w-full lg:w-48"
            >
              <Users className="h-12 w-12 text-primary mx-auto mb-3" />
              <h3 className="font-display text-lg font-bold">Buyer</h3>
              <p className="text-xs text-muted-foreground mt-2">Full dashboard access</p>
              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-1 text-xs text-score-excellent">
                  <Check className="h-3 w-3" />
                  <span>View all findings</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-score-excellent">
                  <Check className="h-3 w-3" />
                  <span>Download reports</span>
                </div>
              </div>
            </motion.div>

            {/* Connector */}
            <div className="hidden lg:block w-16 h-px bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
            <div className="lg:hidden h-8 w-px bg-gradient-to-b from-primary/50 via-primary to-primary/50" />

            {/* PROVENANCE */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-center p-8 rounded-xl border-2 border-primary bg-primary/10 w-full lg:w-56"
            >
              <Shield className="h-14 w-14 text-primary mx-auto mb-3" />
              <h3 className="font-display text-xl font-bold text-primary">PROVENANCE</h3>
              <p className="text-xs text-muted-foreground mt-2">Secure Analysis Layer</p>
              <div className="mt-4 px-4 py-2 rounded-lg bg-primary/10 text-xs text-primary font-medium">
                No IP Leakage
              </div>
            </motion.div>

            {/* Connector */}
            <div className="hidden lg:block w-16 h-px bg-gradient-to-r from-primary/50 via-primary to-primary/50" />
            <div className="lg:hidden h-8 w-px bg-gradient-to-b from-primary/50 via-primary to-primary/50" />

            {/* Seller */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-center p-6 rounded-xl border border-border bg-background w-full lg:w-48"
            >
              <Server className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-display text-lg font-bold">Seller</h3>
              <p className="text-xs text-muted-foreground mt-2">Artefact submission only</p>
              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Check className="h-3 w-3" />
                  <span>Upload access</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Check className="h-3 w-3" />
                  <span>Limited visibility</span>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, idx) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + idx * 0.1 }}
              className="p-5 rounded-xl bg-secondary/30"
            >
              <h4 className="font-display font-semibold mb-2">{feature.title}</h4>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
